import React, { useEffect, useMemo } from 'react';
import '@fontsource/fraunces/400.css';
import '@fontsource/fraunces/500.css';
import '@fontsource/fraunces/600.css';
import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/400-italic.css';
import '@fontsource/newsreader/500.css';
import '@fontsource/newsreader/600.css';
import '@fontsource/hanken-grotesk/400.css';
import '@fontsource/hanken-grotesk/500.css';
import '@fontsource/hanken-grotesk/600.css';
import '@fontsource/hanken-grotesk/700.css';
import type { SiteRagReport } from '../../../types/siteRag';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { buildPowerCoolingAnswers } from '../../../lib/findings/powerCoolingAnswers';
import { powerCoolingInputsAtGeneration } from '../../../lib/screeningSummary/powerCoolingInputsAtGeneration';
import { buildScreeningSummaryModel } from '../../../lib/screeningSummary/buildScreeningSummaryModel';
import { buildNativePrintStylesheet } from '../../../lib/nativePrint/buildNativePrintStylesheet';
import { renderSummaryHtml } from './renderSummaryHtml';
import './screeningSummary.css';

export function ScreeningSummaryDocument({ report, sedAtGen = null, pageSize = 'A4', native = false, children }: {
  report: SiteRagReport; sedAtGen?: SiteEvidenceDocument | null; pageSize?: 'A4' | 'Letter'; native?: boolean; children?: React.ReactNode;
}) {
  const model = useMemo(() => buildScreeningSummaryModel(report, buildPowerCoolingAnswers(powerCoolingInputsAtGeneration(report, sedAtGen))), [report, sedAtGen]);
  const html = useMemo(() => renderSummaryHtml(model), [model]);
  const printCss = buildNativePrintStylesheet({ pageSize: pageSize === 'Letter' ? 'letter' : 'a4', furniture: 'css', runningHead: model.runningHead,
    siteName: model.identity.siteName, pageNumberFormat: 'page-of-total', fontFamily: 'Newsreader, Georgia, serif', pageBackground: '#ffffff',
    demonstrationDisclosure: model.demonstrationDisclosure });
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `Screening Summary — ${model.identity.siteName}`;
    const tags = [['datacrate:summary-version', model.schemaVersion], ['datacrate:builder-version', model.builderVersion],
      ['datacrate:source-report', model.identity.reportId], ['datacrate:source-revision', model.identity.revisionId]]
      .map(([name, content]) => { const tag = document.createElement('meta'); tag.name = name; tag.content = content; document.head.appendChild(tag); return tag; });
    return () => { tags.forEach(tag => tag.remove()); document.title = previousTitle; };
  }, [model]);
  useEffect(() => {
    if (!native) return;
    let cancelled = false;
    document.documentElement.setAttribute('data-native-print', 'true');
    document.documentElement.removeAttribute('data-report-ready');
    void (document.fonts?.ready ?? Promise.resolve()).then(async () => {
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      if (!cancelled) document.documentElement.setAttribute('data-report-ready', 'true');
    });
    return () => { cancelled = true; document.documentElement.removeAttribute('data-native-print'); document.documentElement.removeAttribute('data-report-ready'); };
  }, [native, html, pageSize]);
  return <div data-screening-summary data-summary-size={pageSize === 'Letter' ? 'letter' : 'a4'} lang="en-US">
    <style data-native-print-stylesheet>{printCss}</style>
    {children}
    <div dangerouslySetInnerHTML={{ __html: html }} />
  </div>;
}
export const ScreeningSummaryPrintView = ScreeningSummaryDocument;
